import { useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * A small single-choice control for switching a view between 2–4 mutually exclusive options
 * (a time window, a graph/table view, a status filter).
 *
 * Replaces two byte-for-byte-identical implementations that differed only in their options list
 * (`org-admin/TimeRangeToggle` and the never-mounted `org-admin/GraphTableToggle`). Both animated a
 * sliding pill by measuring `offsetLeft`/`offsetWidth` in a layout effect and re-measuring on
 * `window.resize` — which is wrong twice over: it re-renders on every animation frame of a spring,
 * and `window.resize` never fires when the control is squeezed by a *panel* resize, so the pill
 * silently drifted off its label. The active option is marked with a background instead: same
 * information, no measurement, nothing to desynchronise.
 *
 * Semantics are a radiogroup, so a screen reader announces "2 of 3 selected" and arrow keys move
 * between options — the old buttons were an unlabelled row of `<button>`s with no grouping at all.
 */

export interface SegmentedControlOption<T extends string | number> {
  value: T;
  label: string;
  /** Optional leading icon. Purely decorative — `label` is always rendered. */
  icon?: React.ComponentType<{ className?: string }>;
}

export interface SegmentedControlProps<T extends string | number> {
  /** Labels the group for assistive tech. Required: an unlabelled radiogroup announces nothing. */
  ariaLabel: string;
  value: T;
  options: readonly SegmentedControlOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export function SegmentedControl<T extends string | number>({
  ariaLabel,
  value,
  options,
  onChange,
  disabled,
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  // Arrow keys move the selection, matching the WAI-ARIA radiogroup pattern. Only the checked
  // option is in the tab order, so Tab moves past the whole control rather than through each option.
  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const delta =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!delta) return;
    event.preventDefault();
    const next = (index + delta + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-muted/50 p-0.5',
        // Options never wrap inside the pill — a two-line segmented control stops reading as one
        // control. When four options genuinely cannot fit, the pill scrolls instead of clipping its
        // last option out of reach. Scrollbar hidden: the partially-visible option is the affordance.
        'max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      {options.map((option, index) => {
        const isActive = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={String(option.value)}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={isActive ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
              isActive
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
